import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, ListGroup } from 'react-bootstrap';
import { Project, Goal, Task } from '../types/models';
import { ProjectModal } from './ProjectModal';

interface ProjectManagerProps {
  projects: Project[];
  onProjectCreate: (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'goals'>) => void;
  onGoalCreate: (projectId: string, goal: Omit<Goal, 'id' | 'tasks'>) => void;
  onTaskCreate: (goalId: string, task: Omit<Task, 'id'>) => void;
  onProjectSelect: () => void;
}

export const ProjectManager: React.FC<ProjectManagerProps> = ({
  projects,
  onProjectCreate,
  onGoalCreate,
  onTaskCreate,
  onProjectSelect,
}) => {
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);

  const [goalForm, setGoalForm] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    status: 'pending' as const,
    order: 0,
  });

  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    status: 'pending' as const,
    assignee: '',
    order: 0,
  });

  const handleGoalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject) return;
    onGoalCreate(selectedProject.id, {
      ...goalForm,
      projectId: selectedProject.id,
      startDate: new Date(goalForm.startDate),
      endDate: new Date(goalForm.endDate),
    });
    setShowGoalModal(false);
    setGoalForm({ title: '', description: '', startDate: '', endDate: '', status: 'pending', order: 0 });
  };

  const handleTaskSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGoal) return;
    onTaskCreate(selectedGoal.id, {
      ...taskForm,
      goalId: selectedGoal.id,
      startDate: new Date(taskForm.startDate),
      endDate: new Date(taskForm.endDate),
    });
    setShowTaskModal(false);
    setTaskForm({ title: '', description: '', startDate: '', endDate: '', status: 'pending', assignee: '', order: 0 });
  };

  return (
    <Container className="my-4">
      <Row className="mb-4">
        <Col>
          <h2>Project Tracker</h2>
          <Button variant="primary" onClick={() => setShowProjectModal(true)}>
            + New Project
          </Button>
        </Col>
      </Row>

      <Row>
        {projects.map((project) => (
          <Col key={project.id} md={6} lg={4} className="mb-4">
            <Card>
              <Card.Body>
                <Card.Title>{project.title}</Card.Title>
                <Card.Text>{project.description}</Card.Text>
                <div className="mb-2">
                  <small className="text-muted">
                    {new Date(project.startDate).toLocaleDateString()} - {new Date(project.endDate).toLocaleDateString()}
                  </small>
                </div>
                <div className="d-flex gap-2 flex-wrap mb-3">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => {
                      setSelectedProject(project);
                      setShowGoalModal(true);
                    }}
                  >
                    + Goal
                  </Button>
                  <Button size="sm" variant="outline-success" onClick={() => onProjectSelect()}>
                    View Gantt
                  </Button>
                </div>
                <ListGroup variant="flush">
                  {project.goals.map((goal) => (
                    <ListGroup.Item key={goal.id} className="px-0">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{goal.title}</strong>
                          <div className="text-muted small">{goal.tasks.length} tasks</div>
                        </div>
                        <Button
                          size="sm"
                          variant="link"
                          onClick={() => {
                            setSelectedGoal(goal);
                            setShowTaskModal(true);
                          }}
                        >
                          + Task
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Project Modal */}
      <ProjectModal
        show={showProjectModal}
        onHide={() => setShowProjectModal(false)}
        onProjectCreate={onProjectCreate}
      />

      {/* Goal Modal */}
      <Modal show={showGoalModal} onHide={() => setShowGoalModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Goal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleGoalSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={goalForm.title}
                onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={goalForm.description}
                onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                value={goalForm.startDate}
                onChange={(e) => setGoalForm({ ...goalForm, startDate: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                value={goalForm.endDate}
                onChange={(e) => setGoalForm({ ...goalForm, endDate: e.target.value })}
                required
              />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowGoalModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Create Goal
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Task Modal */}
      <Modal show={showTaskModal} onHide={() => setShowTaskModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleTaskSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Assignee</Form.Label>
              <Form.Control
                type="text"
                value={taskForm.assignee}
                onChange={(e) => setTaskForm({ ...taskForm, assignee: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                value={taskForm.startDate}
                onChange={(e) => setTaskForm({ ...taskForm, startDate: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                value={taskForm.endDate}
                onChange={(e) => setTaskForm({ ...taskForm, endDate: e.target.value })}
                required
              />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowTaskModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Create Task
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};
